<template>
    <div :class="['man-hour-modal', { '--open': open }]">
        <b-sidebar id="man-hour-modal" :visible="open" shadow right bg-variant="white" backdrop no-close-on-backdrop
            no-close-on-esc @change="(val) => $emit('update:open', val)">
            <template #header>
                <div class="man-hour-modal-header__wrapper">
                    <div class="man-hour-modal-header__container">
                        <span class="man-hour-modal-header__title">
                            Apontar Hora Homem
                        </span>

                        <span @click="$emit('update:open', false)" class="man-hour-modal-header__close-icon">
                            <close-icon />
                        </span>
                    </div>

                    <div class="man-hour-modal-header__options">
                        <header-option :active="selectedTab === 'record'" label="Apontar"
                            @click="selectedTab = 'record'" />
                        <header-option :active="selectedTab === 'history'" label="Histórico"
                            @click="selectedTab = 'history'" />
                    </div>
                </div>
            </template>

            <template>
                <div class="man-hour-modal__content">
                    <!-- RECORD TAB -->
                    <div v-if="selectedTab === 'record'" class="man-hour-modal__record-container">
                        <b-form-group label="Operador" class="custom-form-group">
                            <v-select v-model="form.selectedPeople" :options="peopleList" label="name" multiple
                                placeholder="Selecione os operadores" />
                        </b-form-group>

                        <b-form-group label="Turno" class="custom-form-group">
                            <v-select v-model="form.selectedShift" :options="shiftList" label="name"
                                placeholder="Selecione o turno" />
                        </b-form-group>

                        <b-form-group label="Data Início" class="custom-form-group">
                            <flat-pickr v-model="form.startDate" :config="dateConfig" class="form-control"
                                placeholder="Selecione data e hora" />
                        </b-form-group>

                        <b-form-group label="Data Fim" class="custom-form-group">
                            <flat-pickr v-model="form.endDate" :config="dateConfig" class="form-control"
                                placeholder="Selecione data e hora" />
                        </b-form-group>

                        <div class="man-hour-modal__footer-container">
                            <sys-button scheme="square" variant="contained" @click="handleSubmit" :disabled="!isValid">
                                Salvar
                            </sys-button>
                        </div>
                    </div>

                    <!-- HISTORY TAB -->
                    <div v-if="selectedTab === 'history'" class="man-hour-modal__history-container">
                        <div v-if="history.length === 0" class="empty-state">
                            <span>Nenhum histórico encontrado.</span>
                        </div>
                        <div v-else class="history-list">
                            <div v-for="(item, index) in history" :key="index" class="history-item">
                                <div class="history-item__content">
                                    <div class="history-item__row">
                                        <span class="label">Turno:</span>
                                        <span class="value">{{ getShiftName(item.shiftId) }}</span>
                                    </div>
                                    <div class="history-item__row">
                                        <span class="label">Início:</span>
                                        <span class="value">{{ formatDate(item.startTime) }}</span>
                                    </div>
                                    <div class="history-item__row">
                                        <span class="label">Fim:</span>
                                        <span class="value">{{ formatDate(item.endTime) }}</span>
                                    </div>
                                    <div class="history-item__row">
                                        <span class="label">Operadores:</span>
                                        <span class="value">{{ formatOperators(item.operators) }}</span>
                                    </div>
                                </div>
                                <div class="history-item__actions">
                                    <div class="delete-btn" @click="$emit('delete', item.id)">
                                        <TrashIcon />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import { BSidebar, BFormGroup } from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import CloseIcon from "@core/assets/formatted-icons/close-icon.svg";
import TrashIcon from "@/assets/images/icons/trash-icon-2.svg";
import HeaderOption from "./HeaderOption.vue";
import SysButton from "@/@core/components/sys-button/index.vue";
import moment from "moment";

export default {
    name: "ManHourModal",
    components: {
        BSidebar,
        BFormGroup,
        vSelect,
        flatPickr,
        CloseIcon,
        TrashIcon,
        HeaderOption,
        SysButton
    },
    props: {
        open: {
            type: Boolean,
            required: true
        },
        peopleList: {
            type: Array,
            default: () => []
        },
        shiftList: {
            type: Array,
            default: () => []
        },
        usersList: {
            type: Array,
            default: () => []
        },
        history: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            selectedTab: "record",
            form: {
                selectedPeople: [],
                selectedShift: null,
                startDate: null,
                endDate: null
            },
            dateConfig: {
                enableTime: true,
                dateFormat: "Y-m-d H:i",
                time_24hr: true
            }
        };
    },
    computed: {
        isValid() {
            return (
                this.form.selectedPeople.length > 0 &&
                this.form.selectedShift &&
                this.form.startDate &&
                this.form.endDate
            );
        }
    },
    methods: {
        handleSubmit() {
            this.$emit("submit", this.form);
            this.$emit("update:open", false);
            // Reset form or handle success
            this.form = {
                selectedPeople: [],
                selectedShift: null,
                startDate: null,
                endDate: null
            };
        },
        formatDate(date) {
            return moment(date).format("DD/MM/YYYY HH:mm");
        },
        formatOperators(operators) {
            if (!operators || operators.length === 0) return "-";
            return operators.map(op => {
                const guid = op.userGuid || op;

                // Try to find in usersList (API provided by user)
                if (this.usersList && this.usersList.length > 0) {
                    const user = this.usersList.find(u => u.guid === guid);
                    if (user) {
                        return `${user.firstName} ${user.lastName}`;
                    }
                }

                // Fallback to peopleList (context)
                const person = this.peopleList.find(p => (p.id || p.value) === guid);
                return person ? (person.name || person.label) : (op.userName || guid);
            }).join(", ");
        },
        getShiftName(shiftId) {
            if (!shiftId) return "-";
            const shift = this.shiftList.find(s => (s.id || s.value) === shiftId);
            return shift ? shift.name : shiftId;
        }
    }
};
</script>

<style lang="scss">
body:has(.man-hour-modal.--open) {
    overflow: hidden;
}

#man-hour-modal {
    max-height: 100dvh;
    min-width: 600px;
    max-width: 600px;

    .b-sidebar-header {
        padding: 0 !important;
        display: block !important;
    }

    .b-sidebar-body {
        padding: 0 !important;
    }
}

@media (max-width: 768px) {
    #man-hour-modal {
        min-width: 100dvw;
        max-width: 100dvw;
    }
}
</style>

<style lang="scss" scoped>
.man-hour-modal-header__wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;

    .man-hour-modal-header__container {
        display: flex;
        padding: 15.5px 29px;
        border-bottom: 1px solid #cfc4be;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        .man-hour-modal-header__title {
            font-size: 26px;
            font-weight: 600;
            line-height: 36px;
            color: #4c4541;
        }

        .man-hour-modal-header__close-icon {
            cursor: pointer;
            display: flex;
            align-items: center;

            svg {
                width: 20px;
                height: 20px;
                fill: #4c4541;
            }
        }
    }

    .man-hour-modal-header__options {
        display: flex;
        width: 100%;
    }
}

.man-hour-modal__content {
    padding: 0;
    height: 100%;
    overflow-y: auto;

    .man-hour-modal__record-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 24px;
        padding-bottom: 80px;
        /* Make space for fixed footer */

        .custom-form-group {
            margin-bottom: 0;
        }
    }

    .man-hour-modal__history-container {
        display: flex;
        flex-direction: column;
        width: 100%;

        .empty-state {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 200px;
            color: #666;
            font-size: 16px;
        }

        .history-list {
            display: flex;
            flex-direction: column;
            width: 100%;
        }

        .history-item {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 16px 24px;
            background-color: #fff;
            border-bottom: 1px solid #e0e0e0;

            &__content {
                display: flex;
                flex-direction: column;
                gap: 8px;
                flex: 1;
            }

            &__row {
                display: flex;
                gap: 8px;
                font-size: 14px;

                .label {
                    font-weight: 600;
                    color: #4c4541;
                    min-width: 90px;
                }

                .value {
                    color: #333;
                }
            }

            &__actions {
                display: flex;
                align-items: center;
                padding-left: 16px;

                .delete-btn {
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 4px;
                    transition: background-color 0.2s;

                    &:hover {
                        background-color: #ffebee;
                    }

                    svg {
                        width: 20px;
                        height: 20px;
                        fill: #d32f2f;
                    }
                }
            }
        }
    }

    .man-hour-modal__footer-container {
        display: flex;
        padding: 15.5px 29px;
        border-top: 1px solid #cfc4be;
        flex-wrap: nowrap;
        justify-content: flex-end;
        width: 100%;
        gap: 16px;
        background: #fff;
        margin-top: auto;
        /* Pushes footer to bottom if container is flex column */
        position: absolute;
        /* Fix to bottom */
        bottom: 0;
        left: 0;
    }

    .man-hour-modal__history-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;

        .empty-state {
            color: #998f8a;
            font-size: 16px;
        }
    }
}
</style>
